import {isNilOrEmpty} from 'ramda-adjunct';

/**
 * Event names Omnetic sends to GA4.
 *
 * GA4 builds its reports per event name, so this list is meant to stay short: a
 * name earns its place when the thing it describes deserves a report of its own,
 * not merely because it is a different button. Everything else belongs in
 * `event_id` and `params`.
 *
 * The type stays open on purpose. Adding a name here gives it autocomplete and
 * ties it to the GA4 taxonomy we document, but a product area can send its own
 * name without waiting for a change to this shared library.
 */
export type GtmEventName = 'button_click' | 'drag_and_drop' | (string & {});

/**
 * A GA4 event parameter. Scalars arrive in GA4 as they are, so they can be used
 * as dimensions and metrics directly — which is the point of sending them
 * separately instead of folding them into `value`.
 */
export type GtmEventParam = string | number | boolean | null | undefined;

export type GtmEvent = {
  event: GtmEventName;
  event_id: string;
  value?: unknown;
  new_value?: unknown;
  previous_value?: unknown;
  /**
   * Extra GA4 event parameters, passed through untouched. Use snake_case names
   * — that is what GA4 expects and what a custom dimension is registered under.
   *
   * @example
   * pushGtmEvent({
   *   event: 'filter_applied',
   *   event_id: 'sourcing_filter_applied',
   *   params: {filter_name: 'body_type', results_count: 42},
   * });
   */
  params?: Record<string, GtmEventParam>;
};

/**
 * Pushes an event to the Google Tag Manager (GTM) dataLayer.
 *
 * Google Tag Manager (GTM) and Google Analytics 4 (GA4) do not handle `undefined` values well because:
 *  - `undefined` values are **not stored** in the `dataLayer`, meaning values from previous events may persist.
 *  - GA4 does not support `undefined`, which can lead to incomplete or misleading analytics reports.
 *  - Explicitly setting values to `null` ensures that GTM recognizes missing data properly.
 *
 * To prevent these issues, all optional values (`value`, `new_value`, `previous_value`) are replaced
 * with `null` if they are `undefined`.
 *
 * @param eventData - The event data to push to the dataLayer.
 */
export function pushGtmEvent(eventData: GtmEvent) {
  const stringifyIfObjectOrArray = (value: unknown) => {
    if (isNilOrEmpty(value) || Number.isNaN(value)) {
      return null;
    }
    if (typeof value === 'object' || Array.isArray(value)) {
      return JSON.stringify(value);
    }
    return value!.toString();
  };

  const dataLayer = {
    // Parameters go first so that a stray `event` or `value` key among them
    // cannot overwrite what the event itself carries.
    ...eventData.params,
    event: eventData.event,
    event_id: eventData.event_id,
    value: stringifyIfObjectOrArray(eventData?.value),
    new_value: stringifyIfObjectOrArray(eventData?.new_value),
    previous_value: stringifyIfObjectOrArray(eventData?.previous_value),
  };

  if (window && window.dataLayer) {
    window.dataLayer.push(dataLayer);
  }
}
